/**
 * ActivationSetup — first-run, customer-facing billing setup. Shown by the
 * activation gate when the org has no billing profile yet. The customer picks a
 * region (tax pack) and base currency — pre-filled with India GST + INR so most
 * users activate in one tap — and we call POST /org-profile/activate. On success
 * the gate re-checks and the app opens.
 *
 * Region pack + base currency are immutable after activation (backend-enforced),
 * so we keep the choices few and clear.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, ScrollView, Text, TextInput, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../utils/responsive';
import DropdownSelect from '../components/dropdownSelect';
import PrimaryButton from '../components/primaryButton';
import SkIcon from '../components/skIcon';
import {
  listRegionPacks,
  activateOrgProfile,
  RegionPack,
} from '../services/billing/orgProfile.service';

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});

// Base currencies offered at setup. INR first (default). The region pack governs
// tax behaviour; currency is the org's reporting/base currency.
const CURRENCIES: string[][] = [
  ['INR', 'INR · Indian Rupee'],
  ['USD', 'USD · US Dollar'],
  ['EUR', 'EUR · Euro'],
  ['GBP', 'GBP · British Pound'],
  ['AED', 'AED · UAE Dirham'],
  ['SGD', 'SGD · Singapore Dollar'],
  ['AUD', 'AUD · Australian Dollar'],
];

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  return (
    <View style={{ gap: 6 }}>
      <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink2, letterSpacing: 0.2 }}>{label}</Text>
      {children}
      {hint ? <Text style={{ fontFamily: font.sans, fontSize: 11.5, color: colors.ink4, lineHeight: 16 }}>{hint}</Text> : null}
    </View>
  );
};

interface Props {
  onActivated: () => void;
}

const ActivationSetup: React.FC<Props> = ({ onActivated }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const { isWide } = useResponsive();

  const [packs, setPacks] = useState<RegionPack[]>([]);
  const [regionCode, setRegionCode] = useState('IN_GST');
  const [currency, setCurrency] = useState('INR');
  const [gstin, setGstin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    listRegionPacks()
      .then((rows) => {
        if (!alive) return;
        setPacks(rows);
        // Prefer India GST; otherwise fall back to the first available pack.
        if (!rows.some((p) => p.code === 'IN_GST') && rows[0]) setRegionCode(rows[0].code);
      })
      .catch(() => {/* error toast already shown by the service */});
    return () => { alive = false; };
  }, []);

  const regionOptions = useMemo<string[][]>(
    () => (packs.length ? packs.map((p) => [p.code, p.name]) : [['IN_GST', 'India GST']]),
    [packs],
  );
  const isIndia = regionCode === 'IN_GST';

  const activate = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await activateOrgProfile({
        region_pack_code: regionCode,
        base_currency: currency,
        composition_scheme: false,
        ...(isIndia && gstin.trim() ? { gstin: gstin.trim().toUpperCase() } : {}),
      });
      onActivated();
    } catch {
      // error toast already shown by the service; stay on the screen to retry
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper, borderRadius: 8,
    height: 36, paddingHorizontal: 12, fontFamily: font.sans, fontSize: 13, color: colors.ink,
    ...web({ outlineStyle: 'none' }),
  };

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', padding: isWide ? 28 : 16 }}
    >
      <View
        style={{
          maxWidth: 480, width: '100%', backgroundColor: colors.card,
          borderWidth: 1, borderColor: colors.line, borderRadius: 20, overflow: 'hidden',
          paddingVertical: 36, paddingHorizontal: isWide ? 32 : 22,
          ...theme.shadow('md'),
          ...web({ backgroundImage: `radial-gradient(circle at 50% -10%, ${colors.accentSoft}, ${colors.card} 60%)` }),
        }}
      >
        {/* Medallion */}
        <View style={{ alignSelf: 'center', width: 84, height: 84, borderRadius: 26, alignItems: 'center', justifyContent: 'center', marginBottom: 18, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, ...web({ backgroundImage: `radial-gradient(circle at 50% 30%, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 14px 36px -12px ${colors.accent}55` }) }}>
          <SkIcon name="building" size={30} color={colors.accentInk} strokeWidth={2} />
        </View>

        <Text style={{ fontFamily: font.sansBold, fontSize: 22, fontWeight: '700', color: colors.ink, letterSpacing: -0.4, textAlign: 'center' }}>Let’s set up your billing</Text>
        <Text style={{ fontFamily: font.sans, fontSize: 13.5, color: colors.ink3, textAlign: 'center', lineHeight: 21, marginTop: 8, marginBottom: 26 }}>
          Choose your region and currency. This sets up your tax rates, accounts and invoice numbering — it only takes a moment.
        </Text>

        <View style={{ gap: 16 }}>
          <Field label="Region" hint="Sets your tax rules (e.g. GST). This can’t be changed later.">
            <DropdownSelect value={regionCode} options={regionOptions} onChange={setRegionCode} placeholder="Select region" />
          </Field>

          <Field label="Base currency" hint="Your books’ reporting currency. This can’t be changed later.">
            <DropdownSelect value={currency} options={CURRENCIES} onChange={setCurrency} placeholder="Select currency" />
          </Field>

          {isIndia && (
            <Field label="GSTIN (optional)" hint="Add your 15-digit GSTIN now, or later in Settings.">
              <TextInput
                value={gstin}
                onChangeText={(t) => setGstin(t.toUpperCase())}
                placeholder="22AAAAA0000A1Z5"
                placeholderTextColor={colors.ink4}
                autoCapitalize="characters"
                maxLength={15}
                style={inputStyle}
              />
            </Field>
          )}
        </View>

        <View style={{ marginTop: 28 }}>
          <PrimaryButton
            title={submitting ? 'Setting up…' : 'Activate billing'}
            onPress={activate}
            loading={submitting}
            disabled={!regionCode || !currency}
            hoverLift
            rightIcon={!submitting ? <SkIcon name="arrowRight" size={16} color={colors.white} strokeWidth={2.2} /> : undefined}
          />
        </View>

        <Text style={{ fontFamily: font.sans, fontSize: 11.5, color: colors.ink4, textAlign: 'center', lineHeight: 16, marginTop: 16 }}>
          Region and currency are locked in after setup. Everything else stays editable.
        </Text>
      </View>
    </ScrollView>
  );
};

export default ActivationSetup;
